import { useState, useRef, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@geely/ui";
import { Calendar, Check, ChevronsUpDown, Download, ChevronDown, Search, X } from 'lucide-react';
import { cn } from "@geely/ui";
import type { AppointmentStatus } from '@geely/shared';
import type { Store } from '@geely/shared';

interface AppointmentsFilterProps {
  dateFrom: string;
  dateTo: string;
  statusFilter: string;
  search: string;
  statuses: { value: AppointmentStatus; label: string }[];
  showStoreFilter: boolean;
  availableStores: Store[];
  storeFilter: string[];
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onStoreFilterChange: (ids: string[]) => void;
  onExport?: (format: 'csv' | 'excel' | 'pdf') => void;
}

function getToday() {
  return new Date().toISOString().split('T')[0];
}

function getWeekRange() {
  const today = new Date();
  const day = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    from: monday.toISOString().split('T')[0],
    to: sunday.toISOString().split('T')[0],
  };
}

function getMonthRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return {
    from: first.toISOString().split('T')[0],
    to: last.toISOString().split('T')[0],
  };
}

function shortcutClass(active: boolean) {
  return cn(
    'px-[10px] py-1 rounded-full text-[11px] font-medium border outline-none transition-all cursor-pointer',
    active
      ? 'bg-[#eff6ff] border-[#bfdbfe] text-[#2563eb]'
      : 'bg-white border-[#e4e4e7] text-[#71717a] hover:border-[#c4d4f0]',
  );
}

function ExportButton({ onExport }: { onExport?: (format: 'csv' | 'excel' | 'pdf') => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const formats = [
    { key: 'csv' as const,   label: 'CSV',   desc: 'Valores separados por vírgula',  ext: '.csv'  },
    { key: 'excel' as const, label: 'Excel', desc: 'Planilha compatível com Excel',   ext: '.xlsx' },
    { key: 'pdf' as const,   label: 'PDF',   desc: 'Documento com layout formatado',  ext: '.pdf'  },
  ];

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-4 py-[9px] rounded-lg text-[13px] font-semibold bg-[#2563eb] text-white border-none shadow-[0_1px_3px_rgba(37,99,235,.3)] hover:bg-[#1d4ed8] transition-colors cursor-pointer"
      >
        <Download className="size-3.5" />
        Exportar
        <ChevronDown className={cn('size-[11px] opacity-70 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute top-[calc(100%+8px)] right-0 bg-white border border-[#e4e4e7] rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.13)] min-w-[268px] z-[300] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#f0f0f0]">
            <div className="text-[13px] font-bold text-[#18181b]">Exportar registros</div>
            <div className="text-[11px] text-[#a1a1aa] mt-0.5 leading-relaxed">Considera os filtros aplicados</div>
          </div>
          {formats.map((fmt, i) => (
            <button
              key={fmt.key}
              onClick={() => { onExport?.(fmt.key); setOpen(false); }}
              className={cn(
                'flex items-center justify-between w-full px-4 py-[11px] cursor-pointer text-left hover:bg-[#f5f8ff] transition-colors',
                i < formats.length - 1 && 'border-b border-[#f8f8f8]',
              )}
            >
              <div>
                <div className="text-[13px] font-semibold text-[#18181b]">{fmt.label}</div>
                <div className="text-[11px] text-[#a1a1aa] mt-0.5">{fmt.desc}</div>
              </div>
              <span className="text-[11px] font-bold text-[#52525b] bg-[#f4f4f5] px-2 py-[3px] rounded-[5px] tracking-[0.03em] shrink-0 ml-4">
                {fmt.ext}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppointmentsFilter({
  dateFrom,
  dateTo,
  statusFilter,
  search,
  statuses,
  showStoreFilter,
  availableStores,
  storeFilter,
  onDateFromChange,
  onDateToChange,
  onStatusFilterChange,
  onSearchChange,
  onStoreFilterChange,
  onExport,
}: AppointmentsFilterProps) {
  const today = getToday();
  const isToday = dateFrom === today && dateTo === today;
  const week = getWeekRange();
  const isThisWeek = dateFrom === week.from && dateTo === week.to;
  const month = getMonthRange();
  const isThisMonth = dateFrom === month.from && dateTo === month.to;

  const toggleStore = (id: string) => {
    onStoreFilterChange(
      storeFilter.includes(id)
        ? storeFilter.filter((s) => s !== id)
        : [...storeFilter, id],
    );
  };

  const selectedStores = availableStores.filter((s) => storeFilter.includes(s.id));

  const singleStore = !showStoreFilter && availableStores.length === 1 ? availableStores[0] : null;
  const showStoreColumn = showStoreFilter || singleStore !== null;

  const hasFilters = dateFrom || dateTo || search || storeFilter.length > 0 || statusFilter !== 'all';

  const clearAll = () => {
    onDateFromChange('');
    onDateToChange('');
    onSearchChange('');
    onStoreFilterChange([]);
    onStatusFilterChange('all');
  };

  const filterLabel = 'text-[11px] font-bold text-[#52525b] uppercase tracking-[0.05em] mb-[7px]';

  return (
    <div className="bg-white border border-[#dbeafe] rounded-xl shadow-[0_1px_4px_rgba(37,99,235,.07)] mb-4">

      {/* Zona 1: Busca + Exportar */}
      <div className="bg-[#eff6ff] border-b border-[#dbeafe] rounded-t-xl px-[18px] py-[14px] flex items-center gap-2.5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-[15px] text-[#93b4f0] pointer-events-none" />
          <input
            type="text"
            placeholder="Buscar por nome, e-mail ou CPF..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-3 py-[9px] border border-[#bfdbfe] rounded-lg text-[13.5px] text-[#18181b] bg-white outline-none focus:border-[#2563eb] focus:shadow-[0_0_0_3px_rgba(37,99,235,.1)] transition-all"
          />
        </div>
        <ExportButton onExport={onExport} />
      </div>

      {/* Zona 2: Filtros */}
      <div className="px-[18px] py-[13px]">
        <div className={cn(
          'grid gap-3.5 items-start',
          showStoreColumn ? 'grid-cols-[auto_1fr_208px]' : 'grid-cols-[auto_208px]',
        )}>

          {/* Período */}
          <div>
            <div className={filterLabel}>Período</div>
            {/* Date range unificado */}
            <div className="flex items-center h-10 border border-[#e4e4e7] rounded-lg bg-white overflow-hidden w-fit">
              <div className="flex items-center gap-1.5 px-3 py-2">
                <Calendar className="size-3.5 text-[#a1a1aa] shrink-0" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => onDateFromChange(e.target.value)}
                  className="border-none bg-transparent text-[13px] text-[#18181b] outline-none w-[126px]"
                />
              </div>
              <div className="w-px h-[30px] bg-[#e4e4e7] shrink-0" />
              <div className="px-3 py-2">
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom}
                  onChange={(e) => onDateToChange(e.target.value)}
                  className="border-none bg-transparent text-[13px] text-[#18181b] outline-none w-[126px]"
                />
              </div>
            </div>
            {/* Shortcuts */}
            <div className="flex gap-[5px] mt-[7px]">
              <button onClick={() => { onDateFromChange(today); onDateToChange(today); }} className={shortcutClass(isToday)}>
                Hoje
              </button>
              <button onClick={() => { onDateFromChange(week.from); onDateToChange(week.to); }} className={shortcutClass(isThisWeek)}>
                Esta semana
              </button>
              <button onClick={() => { onDateFromChange(month.from); onDateToChange(month.to); }} className={shortcutClass(isThisMonth)}>
                Este mês
              </button>
            </div>
          </div>

          {/* Loja única — somente leitura */}
          {singleStore && (
            <div>
              <div className={filterLabel}>Loja</div>
              <div className="flex items-center gap-2 h-10 px-3 border border-[#e4e4e7] rounded-lg bg-[#fafafa] text-[13px] text-[#52525b]">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                {singleStore.name}
              </div>
            </div>
          )}

          {/* Filtro de lojas (multi-seleção) */}
          {showStoreFilter && (
            <div>
              <div className={filterLabel}>Loja</div>
              <Popover>
                <PopoverTrigger className="flex h-10 w-full items-center justify-between rounded-lg border border-[#e4e4e7] bg-white px-3 py-2 text-sm text-[#18181b] ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                  <span className="truncate text-left text-[13px]">
                    {storeFilter.length === 0
                      ? 'Todas as lojas'
                      : selectedStores.length === 1
                      ? selectedStores[0].name
                      : `${storeFilter.length} lojas selecionadas`}
                  </span>
                  <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar loja..." />
                    <CommandList>
                      <CommandEmpty>Nenhuma loja encontrada.</CommandEmpty>
                      <CommandGroup>
                        {availableStores.map((store) => (
                          <CommandItem
                            key={store.id}
                            value={store.name}
                            onSelect={() => toggleStore(store.id)}
                          >
                            <Check
                              className={cn(
                                'mr-2 size-4',
                                storeFilter.includes(store.id) ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            {store.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedStores.length > 0 && (
                <div className="flex flex-wrap gap-[5px] mt-[7px]">
                  {selectedStores.map((store) => (
                    <span key={store.id} className="inline-flex items-center gap-1 px-[10px] py-1 rounded-full text-[11px] font-medium border border-[#bfdbfe] bg-[#eff6ff] text-[#2563eb]">
                      {store.name}
                      <X
                        className="size-2.5 cursor-pointer opacity-70 hover:opacity-100"
                        onClick={() => toggleStore(store.id)}
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Status */}
          <div>
            <div className={filterLabel}>Status</div>
            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className="text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {statuses.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end mt-[7px]">
              <button
                onClick={clearAll}
                disabled={!hasFilters}
                className={cn(
                  'flex items-center gap-1 px-2 py-[3px] rounded-md text-[11px] font-medium transition-all',
                  hasFilters
                    ? 'text-[#52525b] border border-[#e4e4e7] hover:bg-[#f4f4f5] cursor-pointer'
                    : 'text-[#c4c4c4] border border-transparent cursor-default',
                )}
              >
                <X className="size-2.5" />
                Limpar filtros
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
